/**
 * Sturdfix WMS Offline Support
 * - IndexedDB queue for all writes when offline
 * - Auto-sync on reconnect with green toast confirmation
 * - Amber offline banner "Working offline — X changes queued"
 * - Sync conflict detection and amber persistent toast
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';

const DB_NAME = 'sturdfix_wms_offline';
const STORE_NAME = 'offline_queue';
const DB_VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function addToQueue(entry) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).add(entry);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function getQueue() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const request = tx.objectStore(STORE_NAME).getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function clearQueue() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).clear();
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

async function removeFromQueue(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export function useOffline() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueLength, setQueueLength] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [conflicts, setConflicts] = useState([]);
  const syncingRef = useRef(false);

  const updateQueueLength = useCallback(async () => {
    try {
      const queue = await getQueue();
      setQueueLength(queue.length);
    } catch (err) { /* queue check non-critical */ }
  }, []);

  const syncQueue = useCallback(async () => {
    if (syncingRef.current || !navigator.onLine) return;
    syncingRef.current = true;
    setSyncing(true);

    try {
      const queue = await getQueue();
      if (queue.length === 0) return { synced: 0, conflicts: 0 };

      const API_BASE = process.env.REACT_APP_BACKEND_URL;
      let successCount = 0;
      let conflictCount = 0;
      const newConflicts = [];

      // First, try to batch sync placement actions via the sync/queue endpoint
      const placementActions = queue.filter(e => e.url?.includes('/place') || e.data?.type === 'place');
      
      if (placementActions.length > 0) {
        try {
          const token = localStorage.getItem('wms_token');
          const response = await fetch(`${API_BASE}/api/sync/queue`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({
              actions: placementActions.map(a => ({
                type: 'place',
                slot_address: a.data?.slot_address,
                pallet_id: a.data?.pallet_id,
                ...a.data
              }))
            }),
          });
          
          if (response.ok) {
            const result = await response.json();
            successCount += result.synced || 0;
            conflictCount += result.conflicts || 0;
            
            if (result.conflict_details) {
              newConflicts.push(...result.conflict_details);
            }
            
            // Remove synced placement actions from queue
            for (const action of placementActions) {
              await removeFromQueue(action.id);
            }
          }
        } catch (err) {
          console.error('Batch sync failed:', err);
        }
      }

      // Process remaining non-placement actions individually
      const otherActions = queue.filter(e => !e.url?.includes('/place') && e.data?.type !== 'place');
      
      for (const entry of otherActions) {
        try {
          const response = await fetch(`${API_BASE}${entry.url}`, {
            method: entry.method,
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${entry.token}`,
            },
            body: entry.data ? JSON.stringify(entry.data) : undefined,
          });
          if (response.ok) {
            await removeFromQueue(entry.id);
            successCount++;
          }
        } catch {
          // Stop on network error
          break;
        }
      }

      await updateQueueLength();
      setConflicts(newConflicts);

      // Show appropriate toast
      if (conflictCount > 0) {
        // Amber persistent toast for conflicts
        toast.warning(
          `${successCount} changes synced — ${conflictCount} conflict(s) detected. Tap to resolve.`,
          {
            duration: Infinity, // Persistent - doesn't auto-dismiss
            action: {
              label: 'View Conflicts',
              onClick: () => {
                window.location.href = '/admin?tab=conflicts';
              }
            }
          }
        );
      } else if (successCount > 0) {
        // Green toast for successful sync
        toast.success(`${successCount} changes synced successfully`);
      }

      return { synced: successCount, conflicts: conflictCount };
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, [updateQueueLength]);

  const queueAction = useCallback(async (method, url, data) => {
    const entry = {
      method,
      url,
      data,
      timestamp: new Date().toISOString(),
      token: localStorage.getItem('wms_token'),
    };
    await addToQueue(entry);
    await updateQueueLength();
    return { queued: true };
  }, [updateQueueLength]);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      syncQueue();
    };
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Listen for SW sync message
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data?.type === 'SYNC_OFFLINE_QUEUE') {
          syncQueue();
        }
      });
    }

    updateQueueLength();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [updateQueueLength, syncQueue]);

  return { 
    isOnline, 
    queueLength, 
    syncing, 
    conflicts,
    queueAction, 
    syncQueue, 
    updateQueueLength 
  };
}
